import { useState } from "react";
import { money, pct, marginColor, marginBg } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Save, Copy, FileText, Stethoscope, Sparkles, Pencil, ShieldAlert, Loader2 } from "lucide-react";

function Row({ label, value, strong, accent, sub }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${strong ? "font-semibold" : "text-muted-foreground"}`}>{label}</span>
      <span className={`metric-num text-sm ${strong ? "font-bold" : ""} ${accent || ""}`}>
        {value}{sub && <span className="ml-1 text-xs text-muted-foreground">{sub}</span>}
      </span>
    </div>
  );
}

function OverrideDialog({ financial, carrierCost, onApply }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(financial?.sell_price ?? "");
  const [reason, setReason] = useState("");
  const apply = () => {
    if (!reason.trim()) return;
    onApply(Number(price), reason.trim());
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="override-button" className="h-8">
          <Pencil className="mr-1.5 h-3.5 w-3.5" /> Override
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Manual Price Override</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-caps">Override Sell Price ($)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5 h-10" data-testid="override-price" />
          </div>
          <div>
            <Label className="label-caps">Reason (required)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1.5" data-testid="override-reason" placeholder="Why is the price being overridden?" />
            {!reason.trim() && <p className="mt-1 text-xs text-[hsl(var(--destructive))]">A reason is required to override.</p>}
          </div>
          <p className="text-xs text-muted-foreground">Carrier cost: {money(carrierCost)}. Margin recalculates on apply.</p>
        </div>
        <DialogFooter>
          <Button onClick={apply} disabled={!reason.trim()} data-testid="override-apply" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Apply Override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FinancialBreakdown({
  result, selectedRate, financial, onApplyOverride, onClearOverride,
  onSave, onDuplicate, onCustomer, onDiagnostics, onExplain, saving, savedQuoteId, savedQuoteNumber,
}) {
  if (!result || !selectedRate || !financial) {
    return (
      <div className="border border-border bg-card p-4 text-sm text-muted-foreground">
        Financial breakdown appears after rates are calculated.
      </div>
    );
  }

  const cost = selectedRate.total_carrier_cost;
  const acc = selectedRate.accessorial_charges || 0;

  return (
    <div className="border border-border bg-card" data-testid="financial-breakdown">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="font-heading text-base font-bold tracking-tight">Financial Breakdown</h3>
        <span className="label-caps">{selectedRate.carrier_name}</span>
      </div>

      <div className={`border-b border-border p-4 ${marginBg(financial.margin_health)}`}>
        <div className="flex items-center justify-between">
          <span className="label-caps">Final Sell Price</span>
          {financial.is_override && (
            <span className="rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[hsl(var(--warning))]">Override</span>
          )}
        </div>
        <div className="metric-num mt-1 text-3xl font-black">{money(financial.sell_price)}</div>
        <div className={`mt-1 flex items-center gap-2 text-sm font-semibold ${marginColor(financial.margin_health)}`}>
          {financial.margin_health === "red" && <ShieldAlert className="h-4 w-4" />}
          Margin {money(financial.margin_dollars)} · {pct(financial.margin_pct)}
        </div>
        {financial.margin_health === "red" && (
          <p className="mt-1 text-xs text-[hsl(var(--destructive))]">Below minimum margin — manual review required.</p>
        )}
      </div>

      <div className="px-4 py-3">
        <Row label="Raw carrier cost" value={money(cost)} />
        <Row label="— Linehaul" value={money(selectedRate.linehaul)} />
        <Row label="— Fuel surcharge" value={money(selectedRate.fuel_surcharge)} />
        {(selectedRate.accessorial_detail && selectedRate.accessorial_detail.length > 0) ? (
          selectedRate.accessorial_detail.map((a, i) => (
            <Row key={i} label={`— ${a.name}`} value={money(a.amount)} />
          ))
        ) : (
          <Row label="— Accessorials" value={money(acc)} />
        )}
        <Row label="— Negotiated discount" value={`-${money(selectedRate.discount)}`} accent="text-[hsl(var(--success))]" />
        <div className="my-2 border-t border-border" />
        <Row label="Handling / admin fee" value={money(financial.handling_fee)} />
        {financial.insurance_fee > 0 && <Row label="Insurance / protection" value={money(financial.insurance_fee)} />}
        <Row label="Markup" value={`${financial.markup_pct}%`} />
        <Row label="Min margin rule" value={`${financial.min_margin_pct}%`} />
        <Row label="Min freight charge" value={money(financial.min_freight_charge)} />
        <div className="my-2 border-t border-border" />
        <Row label="Suggested customer charge" value={money(financial.computed_sell_price)} strong />
        <Row label="Gross margin $" value={money(financial.margin_dollars)} strong accent={marginColor(financial.margin_health)} />
        <Row label="Gross margin %" value={pct(financial.margin_pct)} strong accent={marginColor(financial.margin_health)} />
      </div>

      <div className="flex items-center gap-2 border-t border-border px-4 py-3">
        <OverrideDialog financial={financial} carrierCost={cost} onApply={onApplyOverride} />
        {financial.is_override && (
          <Button variant="ghost" size="sm" onClick={onClearOverride} className="h-8 text-muted-foreground">Clear</Button>
        )}
      </div>

      {savedQuoteId && savedQuoteNumber && (
        <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground" data-testid="autosaved-indicator">
          Auto-saved as <span className="font-bold text-foreground">{savedQuoteNumber}</span> — already in Quote History.
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 border-t border-border p-4">
        <Button onClick={onSave} disabled={saving} data-testid="save-quote-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> {savedQuoteId ? "Update" : "Save Quote"}</>}
        </Button>
        <Button variant="outline" onClick={onCustomer} data-testid="customer-quote-button"><FileText className="mr-1.5 h-4 w-4" /> Customer Quote</Button>
        <Button variant="outline" onClick={onDiagnostics} data-testid="diagnostics-button"><Stethoscope className="mr-1.5 h-4 w-4" /> Diagnostics</Button>
        <Button variant="outline" onClick={onExplain} data-testid="explain-button"><Sparkles className="mr-1.5 h-4 w-4" /> Explain (AI)</Button>
        {savedQuoteId && (
          <Button variant="ghost" onClick={onDuplicate} data-testid="duplicate-quote-button" className="col-span-2"><Copy className="mr-1.5 h-4 w-4" /> Duplicate Quote</Button>
        )}
      </div>
    </div>
  );
}
